import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

interface PaymentData {
  SignData: string;
  NicepayReserved: string;
  DirectShowOpt: string;
  Period: string;
  TransType: string;
  PayMethod: string;
  Amt: string;
  MID: string;
  EdiDate: string;
  ReturnURL: string;
  GoodsName: string;
  Moid: string;
  [key: string]: string;
}

interface FormFields {
  [key: string]: string;
}

// ============================================
// 상수
// ============================================
const NICEPAY_SCRIPT_URL =
  'https://pg-web.nicepay.co.kr/v3/common/js/nicepay-pgweb.js';
const FORM_NAME = 'payForm';

/**
 * URL 쿼리 스트링에서 결제 데이터 파싱
 */
const parsePaymentData = (dataParam: string): PaymentData | null => {
  try {
    const paymentData = JSON.parse(decodeURIComponent(dataParam));
    return paymentData;
  } catch (error) {
    console.error('💥 Failed to parse payment data:', error);
    return null;
  }
};

/**
 * Form 엘리먼트 찾기
 */
const findPaymentForm = (): HTMLFormElement | null => {
  const form = document.querySelector(
    `form[name="${FORM_NAME}"]`,
  ) as HTMLFormElement;

  if (!form) {
    console.error(`❌ Payment form not found: "${FORM_NAME}"`);
  }

  return form;
};

/**
 * FormData를 일반 객체로 변환
 */
const formDataToObject = (formData: FormData): Record<string, string> => {
  const data: Record<string, string> = {};

  formData.forEach((value, key) => {
    data[key] = String(value);
  });

  return data;
};

/**
 * Hidden input 생성
 */
const createHiddenInput = (name: string, value: string): HTMLInputElement => {
  const input = document.createElement('input');
  input.type = 'hidden';
  input.name = name;
  input.value = value;
  return input;
};

/**
 * 기존 Form 제거
 */
const removeExistingForm = (formName: string = FORM_NAME): void => {
  const existingForm = document.querySelector(`form[name="${formName}"]`);
  if (existingForm) {
    existingForm.remove();
  }
};

// ============================================
// Form 생성 함수
// ============================================

/**
 * 결제용 Form 필드 생성
 * 확장 가능: 필요한 필드를 추가로 전달 가능
 */
const buildPaymentFormFields = (
  paymentData: PaymentData,
  additionalFields?: FormFields,
): FormFields => {
  const baseFields: FormFields = {};

  Object.entries(paymentData).forEach(([key, value]) => {
    baseFields[key] = value;
  });

  return {
    ...baseFields,
    ...additionalFields,
  };
};

/**
 * 결제용 Form 생성
 */
const createPaymentForm = (
  fields: FormFields,
  formName: string = FORM_NAME,
): HTMLFormElement => {
  const form = document.createElement('form');
  form.setAttribute('name', formName);
  form.setAttribute('acceptCharset', 'EUC-KR');
  form.setAttribute('method', 'POST');
  form.style.display = 'none';

  Object.entries(fields).forEach(([key, value]) => {
    form.appendChild(createHiddenInput(key, value || ''));
  });

  return form;
};

// ============================================
// 메인 컴포넌트
// ============================================

const NicePaymentScreen = () => {
  const [searchParams] = useSearchParams();

  useEffect(() => {
    initializePayment();
  }, []);

  // ============================================
  // 1. 초기화
  // ============================================

  const initializePayment = () => {
    const dataParam = searchParams.get('data');

    if (!dataParam) {
      alert('결제 데이터가 없습니다.');
      return;
    }

    const paymentData = parsePaymentData(dataParam);

    if (!paymentData) {
      alert('잘못된 결제 데이터입니다.');
      return;
    }

    loadNicePayScript(paymentData);
  };

  // ============================================
  // 2. NICEPAY 스크립트 로드
  // ============================================

  const loadNicePayScript = (paymentData: PaymentData) => {
    if (typeof window.goPay === 'function') {
      setupCallbacksAndPay(paymentData);
      return;
    }

    const script = document.createElement('script');
    script.src = NICEPAY_SCRIPT_URL;
    script.async = true;

    script.onload = () => {
      setupCallbacksAndPay(paymentData);
    };

    script.onerror = () => {
      console.error('💥 Failed to load NICEPAY SDK');
      alert('결제 시스템 로드에 실패했습니다.');
    };

    document.head.appendChild(script);
  };

  // ============================================
  // 3. 콜백 설정 및 Form 생성
  // ============================================

  const setupCallbacksAndPay = (paymentData: PaymentData) => {
    setupNicepaySubmit();
    setupNicepayClose();
    createFormAndStartPayment(paymentData);
  };

  // ============================================
  // 4. nicepaySubmit 콜백 (결제 성공)
  // ============================================

  const setupNicepaySubmit = () => {
    window.nicepaySubmit = async () => {
      const payForm = findPaymentForm();
      if (!payForm) {
        alert('Form을 찾을 수 없습니다.');
        return;
      }

      try {
        const formData = new FormData(payForm);
        const data = formDataToObject(formData);

        const returnURL = data.ReturnURL;

        if (!returnURL) {
          alert('ReturnURL을 찾을 수 없습니다.');
          console.error('💥 Missing ReturnURL in form data:', data);
          return;
        }

        submitToApprovalServer(payForm, returnURL);
      } catch (error) {
        console.error('💥 Authorization processing error:', error);
        payForm.remove();
        alert('승인 처리 중 오류가 발생했습니다.');
      }
    };
  };

  // ============================================
  // 5. nicepayClose 콜백 (결제 취소)
  // ============================================

  const setupNicepayClose = () => {
    window.nicepayClose = async () => {
      const payForm = findPaymentForm();

      if (!payForm) {
        return;
      }

      try {
        const formData = new FormData(payForm);
        const data = formDataToObject(formData);

        const returnURL = data.ReturnURL;

        if (!returnURL) {
          alert('ReturnURL을 찾을 수 없습니다.');
          payForm.remove();
          return;
        }

        submitToApprovalServer(payForm, returnURL);
      } catch (error) {
        console.error('💥 Cancellation processing error:', error);
        payForm.remove();
        alert('취소 처리 중 오류가 발생했습니다.');
      }
    };
  };

  // ============================================
  // 6. 승인팀 서버로 Form Submit
  // ============================================

  const submitToApprovalServer = (
    payForm: HTMLFormElement,
    returnURL: string,
  ) => {
    payForm.action = returnURL;
    payForm.method = 'POST';
    payForm.submit();
  };

  // ============================================
  // 7. Form 생성 및 결제 시작
  // ============================================

  const createFormAndStartPayment = (paymentData: PaymentData) => {
    removeExistingForm();

    const fields = buildPaymentFormFields(paymentData, {
      // 추가 필드 예시 (필요시 여기에 추가)
    });

    const form = createPaymentForm(fields);

    document.body.appendChild(form);

    executePayment(form);
  };

  const executePayment = (form: HTMLFormElement) => {
    if (typeof window.goPay === 'function') {
      window.goPay(form);
    } else {
      console.error('💥 Payment gateway function not available');
      alert('결제 시스템 초기화에 실패했습니다.');
    }
  };

  // ============================================
  // 8. 렌더링
  // ============================================

  return (
    <div
      style={{
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <div style={{ marginTop: '20px' }}>
        <div className="loading-spinner"></div>
      </div>
    </div>
  );
};

export default NicePaymentScreen;
